import type { FunctionComponent, ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { useMcpContext } from '../McpContextProvider';
import { bitbucketWorkspaceSelectorMessages } from './messages';
import { orgMismatchMessages } from '../orgMismatchMessages';
import MultiSelect from '../MultiSelect/MultiSelect';
import FieldLabel from '../MultiSelect/FieldLabel';
import {
  getAvatarOptionChipColor,
  getAvatarOptionIcon,
} from '../MultiSelect/avatarOption';
import { grantedNoPermissions } from '../domain/policyState';
import type { BitbucketWorkspace } from '#definitions/consent-info';

const BITBUCKET_SELECT_INPUT_ID =
  'oauth-consent-mcp-bitbucket-workspace-select';

// Pure functions of the item, so they live at module scope rather than being
// rebuilt in the component body.
const toValue = (ws: BitbucketWorkspace): string => ws.ari;
const getLabel = (ws: BitbucketWorkspace): string => ws.name;

/**
 * Bitbucket workspace picker for the MCP consent screen.
 *
 * Rendered above the Atlassian site selector whenever /consent/info returned at
 * least one workspace. An empty list hides the field entirely rather than
 * showing one with nothing to pick. There is no
 * `oauth-consent_bitbucket-mcp-workspace` check in this flow — see
 * useBitbucketWorkspaces. The selected workspace's ARI is submitted as
 * an additional `selectedWorkspace` param on Accept (see McpFlowParameters).
 *
 * A successful check that grants nothing is reported in this field's own message
 * slot, reading only `policies.bitbucket` so a site result never surfaces here.
 * A *failed* request is shown full-panel by McpPolicyAlerts instead, and the
 * field stays mounted throughout so the selection can be changed and retried.
 */
const BitbucketWorkspaceSelector: FunctionComponent =
  (): ReactElement | null => {
    const {
      isBitbucketWorkspaceAccessEnabled,
      availableBitbucketWorkspaces,
      selectedBitbucketWorkspace,
      setSelectedBitbucketWorkspace,
      policies,
      hasOrgMismatch,
    } = useMcpContext();
    const { formatMessage } = useIntl();

    // Hidden unless there is at least one workspace to grant.
    if (!isBitbucketWorkspaceAccessEnabled) {
      return null;
    }

    return (
      <MultiSelect<BitbucketWorkspace, string>
        inputId={BITBUCKET_SELECT_INPUT_ID}
        items={availableBitbucketWorkspaces}
        value={
          selectedBitbucketWorkspace ? [selectedBitbucketWorkspace.ari] : []
        }
        toValue={toValue}
        getLabel={getLabel}
        getIcon={getAvatarOptionIcon}
        getChipColor={getAvatarOptionChipColor}
        message={
          // Ahead of the policy result: the two selections cannot be granted
          // together whatever the check returned, and changing one of them is
          // the only way forward — so that is what the field should say.
          hasOrgMismatch
            ? formatMessage(orgMismatchMessages.orgMismatch)
            : grantedNoPermissions(policies.bitbucket)
              ? formatMessage(bitbucketWorkspaceSelectorMessages.noPermissions)
              : undefined
        }
        placeholder={formatMessage(
          bitbucketWorkspaceSelectorMessages.placeholder,
        )}
        renderLabel={() => (
          <FieldLabel>
            {formatMessage(bitbucketWorkspaceSelectorMessages.label)}
          </FieldLabel>
        )}
        onChange={(_aris, items) =>
          setSelectedBitbucketWorkspace(items[0] ?? null)
        }
      />
    );
  };

export default BitbucketWorkspaceSelector;
